import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../states/hooks";
import {
  clearError,
  createTeacher,
  deleteTeacher,
  fetchTeachers,
  selectTeachers,
  selectTeachersError,
  selectTeachersLoading,
  selectTeachersPage,
  selectTeachersPageSize,
  selectTeachersTotal,
  setPage,
  setPageSize,
  updateTeacher,
} from "../../states/teachers/TeachersSlice";
import {
  CreateTeacherInput,
  TeacherData,
  UpdateTeacherInput,
} from "../../model/TeacherData";
import { useBase } from "../../context/BaseContext";
import EditTeacherModal from "../../components/EditTeacherModal";
import { DeleteIcon, EditIcon, EmptyBoxIcon } from "../../assets/svg";
import "../dashboard/Dashboard.css";

const TeachersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const teachers = useAppSelector(selectTeachers);
  const isLoading = useAppSelector(selectTeachersLoading);
  const error = useAppSelector(selectTeachersError);
  const totalCount = useAppSelector(selectTeachersTotal);
  const currentPage = useAppSelector(selectTeachersPage);
  const pageSize = useAppSelector(selectTeachersPageSize);
  const { showError, showSuccess, showConfirm } = useBase();

  const [modal, setModal] = useState<{
    isOpen: boolean;
    teacher: TeacherData | null;
  }>({ isOpen: false, teacher: null });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const reload = () =>
    dispatch(
      fetchTeachers({
        page: currentPage,
        pageSize,
        searchKey: debouncedSearchTerm,
      }),
    );

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage, pageSize, debouncedSearchTerm]);

  useEffect(() => {
    if (error) {
      showError("Lỗi", error);
      dispatch(clearError());
    }
  }, [error, showError, dispatch]);

  const closeModal = () => setModal({ isOpen: false, teacher: null });

  const handleCreate = async (data: CreateTeacherInput) => {
    const result = await dispatch(createTeacher(data));
    if (createTeacher.fulfilled.match(result)) {
      showSuccess("Thành công", "Đã thêm giáo viên");
      closeModal();
      reload();
    }
  };

  const handleUpdate = async (data: UpdateTeacherInput) => {
    if (!modal.teacher) return;
    const result = await dispatch(
      updateTeacher({ id: modal.teacher.id, data }),
    );
    if (updateTeacher.fulfilled.match(result)) {
      showSuccess("Thành công", "Đã cập nhật giáo viên");
      closeModal();
    }
  };

  const handleDelete = (t: TeacherData) => {
    showConfirm(
      "Xoá giáo viên",
      `Xoá giáo viên "${t.name || t.userName}"? Tài khoản này sẽ không đăng nhập được nữa.`,
      async () => {
        const result = await dispatch(deleteTeacher(t.id));
        if (deleteTeacher.fulfilled.match(result)) {
          showSuccess("Thành công", "Đã xoá giáo viên");
          reload();
        }
      },
    );
  };

  return (
    <>
      <EditTeacherModal
        isOpen={modal.isOpen}
        teacher={modal.teacher}
        onClose={closeModal}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
      />
      <div className="dashboard-container">
        <div className="dashboard-stats-grid">
          <div className="dashboard-stat-card">
            <div className="dashboard-stat-card-content">
              <div className="dashboard-stat-flex">
                <div className="dashboard-stat-info">
                  <dl>
                    <dt className="dashboard-stat-label">Tổng số giáo viên</dt>
                    <dd className="dashboard-stat-value">{totalCount}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div />
          <Button
            variant="contained"
            color="primary"
            onClick={() => setModal({ isOpen: true, teacher: null })}
            sx={{
              textTransform: "none",
              height: "fit-content",
              alignSelf: "center",
            }}
          >
            + Thêm giáo viên
          </Button>
        </div>

        <div className="dashboard-table-container">
          <div className="dashboard-table-header">
            <input
              type="text"
              placeholder="Tìm theo tên hoặc tên đăng nhập..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                dispatch(setPage(1));
              }}
              style={{
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                width: "300px",
                outline: "none",
              }}
            />
          </div>

          {isLoading ? (
            <div className="dashboard-loading">
              <div className="dashboard-loading-content">
                <div className="dashboard-loading-spinner" />
                <p className="dashboard-loading-text">Đang tải giáo viên...</p>
              </div>
            </div>
          ) : teachers.length === 0 ? (
            <div className="dashboard-empty">
              <EmptyBoxIcon className="dashboard-empty-icon" />
              <h3 className="dashboard-empty-title">
                {searchTerm ? "Không tìm thấy giáo viên" : "Chưa có giáo viên"}
              </h3>
              <p className="dashboard-empty-description">
                {searchTerm
                  ? `Không có giáo viên nào khớp "${searchTerm}"`
                  : 'Bấm "Thêm giáo viên" để bắt đầu.'}
              </p>
            </div>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
              <Table aria-label="teachers table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>Họ tên</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Tên đăng nhập
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Số điện thoại
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="center">
                      Hành động
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teachers.map((t) => (
                    <TableRow
                      key={t.id}
                      sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
                    >
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Avatar sx={{ bgcolor: "#3b82f6" }}>
                            {(t.name || t.userName).charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography>{t.name || "N/A"}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{t.userName}</TableCell>
                      <TableCell>{t.phoneNumber}</TableCell>
                      <TableCell>{t.email || ""}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => setModal({ isOpen: true, teacher: t })}
                          color="primary"
                          size="small"
                          title="Sửa"
                        >
                          <EditIcon style={{ width: 20, height: 20 }} />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(t)}
                          color="error"
                          size="small"
                          title="Xoá"
                        >
                          <DeleteIcon style={{ width: 20, height: 20 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={currentPage - 1}
                onPageChange={(_, newPage) => dispatch(setPage(newPage + 1))}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(event) => {
                  dispatch(setPageSize(parseInt(event.target.value, 10)));
                  dispatch(setPage(1));
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </TableContainer>
          )}
        </div>
      </div>
    </>
  );
};

export default TeachersPage;
